import { Stack } from '@mui/material';

import { BadInputError } from '@watershed/errors/BadInputError';
import ChevronDownIcon from '@watershed/icons/components/ChevronDown';

import Button, { ButtonProps } from './Button';
import DropdownMenu, { type DropdownItem } from './DropdownMenu';

/**
 * NOTE: Not all <Button /> props are supported for item #1 for now because
 * we need to map each one from DropdownItem interface to ButtonProps.
 * If something is missing, please add it into firstButton's rendering!
 */
export type SplitButtonDropdownItem = Omit<DropdownItem, 'onSelect'> & {
  /**
   * This applies to the first item (the <Button />) in the list only!
   *
   * If href is provided, use that, otherwise use the onSelect.
   * This is some custom handling for the first item, the <Button />
   * to support accessibility and opening in new tab (which onSelect doesn't)
   */
  href?: ButtonProps['href'];
  /**
   * We require onSelect to be provided in order to ensure the href support
   * doesn't break anything without an engineer noticing.
   */
  onSelect: (id: string) => void;
};

/**
 * Renders a button with the first item in the list fully expanded and the
 * rest of the items grouped into a condensed dropdown menu.
 *
 * If only one item is provided, it will render as a regular button.

 */
export default function SplitButtonDropdown({
  items,
  size,
  'data-testid': dataTestId,
  sx,
  triggerButtonVariant,
}: {
  /**
   * Must be length 1+!
   *
   * The first item in the array will just render as a Button.
   * If there are more than one item, the rest will render as a joined
   * split button, under a DropdownMenu.
   */
  items: Array<SplitButtonDropdownItem>;
  size?: ButtonProps['size'];
  sx?: ButtonProps['sx'];
  'data-testid'?: string;
  triggerButtonVariant?: 'primary' | 'error' | 'success';
}) {
  BadInputError.invariant(
    items.length > 0,
    'items must be an array of at least one item'
  );

  const firstItem = items[0];
  const restItems = items.slice(1);

  const hasDropdown = restItems.length > 0;
  const firstButton = (
    <Button
      color={firstItem.variant}
      data-testid={firstItem.testId}
      startIcon={firstItem.Icon ? <firstItem.Icon /> : undefined}
      sx={
        hasDropdown
          ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
          : undefined
      }
      disabled={firstItem.disabled}
      // If href is provided, use that, otherwise use the onSelect
      href={firstItem.href}
      onClick={
        firstItem.href ? undefined : () => firstItem.onSelect(firstItem.id)
      }
      tooltip={firstItem.tooltip}
      size={size}
    >
      {firstItem.label}
    </Button>
  );

  return (
    <Stack direction="row" data-testid={dataTestId} sx={sx}>
      {firstButton}
      {hasDropdown && (
        <DropdownMenu
          dense
          trigger={<ChevronDownIcon />}
          triggerEndIcon={null}
          triggerSx={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            minWidth: '32px',
            marginLeft: '-1px',
            px: 1,
          }}
          buttonProps={{ size, color: triggerButtonVariant }}
          items={restItems}
        />
      )}
    </Stack>
  );
}
